"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { useEffect, useState, useSyncExternalStore } from "react";

import {
  browserAnalyticsConsent,
  browserAnalyticsPolicy,
  resolveAnalyticsMode,
  type AnalyticsRegionPolicy,
  subscribeAnalyticsConsent,
} from "@/lib/analytics-consent";
import { updateAnalyticsConsent } from "@/lib/posthog-client";
import { isPublicTelemetryPath } from "@/lib/telemetry";
import type { Locale } from "@/i18n/config";
import { dictionaryFor } from "@/i18n/dictionaries";

export const openPrivacyChoicesEvent = "ta3na:open-privacy-choices";

const subscribeHydration = () => () => undefined;
const browserHydrationSnapshot = () => true;
const serverHydrationSnapshot = () => false;

type AnalyticsConsentBannerProps = {
  locale?: Locale;
  policy?: AnalyticsRegionPolicy;
};

export function AnalyticsConsentBanner({ locale = "en", policy = browserAnalyticsPolicy() }: AnalyticsConsentBannerProps = {}) {
  const copy = dictionaryFor(locale).global.analytics;
  const pathname = usePathname();
  const hydrated = useSyncExternalStore(
    subscribeHydration,
    browserHydrationSnapshot,
    serverHydrationSnapshot,
  );
  const choice = useSyncExternalStore(subscribeAnalyticsConsent, browserAnalyticsConsent, () => "unknown" as const);
  const [manuallyOpened, setManuallyOpened] = useState(false);
  const mode = resolveAnalyticsMode(choice, policy);
  const open = manuallyOpened || choice === "unknown";

  useEffect(() => {
    const show = () => setManuallyOpened(true);
    window.addEventListener(openPrivacyChoicesEvent, show);
    return () => window.removeEventListener(openPrivacyChoicesEvent, show);
  }, []);

  if (!hydrated || !isPublicTelemetryPath(pathname) || !open) return null;

  function choose(consent: "denied" | "granted") {
    updateAnalyticsConsent(consent);
    setManuallyOpened(false);
  }

  return (
    <aside aria-labelledby="analytics-consent-title" className="analytics-consent">
      <div className="analytics-consent__copy">
        <h2 id="analytics-consent-title">{copy.title}</h2>
        <p>
          {copy.description}{" "}
          <Link className="analytics-consent__privacy-link" href="/privacy" lang={locale === "en" ? undefined : "en"}>{copy.privacyLink}</Link>
        </p>
        {choice !== "unknown" ? (
          <p className="analytics-consent__current">
            {mode === "granted" ? copy.currentAccepted : null}
            {mode === "denied" ? copy.currentRejected : null}
          </p>
        ) : null}
      </div>
      <div className="analytics-consent__actions">
        <button aria-label={copy.rejectLabel} className="button button--small button--secondary" onClick={() => choose("denied")} type="button">{copy.reject}</button>
        <button aria-label={copy.acceptLabel} className="button button--small" onClick={() => choose("granted")} type="button">{copy.accept}</button>
      </div>
    </aside>
  );
}
